import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import Annotation from "~/components/build/Show/components/Annotation";
import { JobAnnotationLink } from "~/components/build/Show/components/annotations/JobAnnotationLink";
import { JobAnnotationsEmptyState } from "~/components/build/Show/components/AnnotationsEmptyState";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { allJobAnnotationsQueryOptions } from "~/components/build/Show/lib/queries/allJobAnnotations";
import type { JobAnnotation } from "~/components/build/Show/lib/types";
import { useReactRouterNavigateHandler } from "~/components/build/Show/lib/useReactRouterNavigateHandler";
import Spinner from "~/components/shared/Spinner";

export default function JobAnnotations() {
  const { build } = useBuild();
  const navigateClickHandler = useReactRouterNavigateHandler();

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery(
    allJobAnnotationsQueryOptions(build, {
      placeholderData: keepPreviousData,
    }),
  );

  const handleShowMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const paginatedAnnotations = useMemo(() => {
    return (data?.pages || []).flatMap((page) => page.records);
  }, [data?.pages]);

  if (isLoading) {
    return (
      <div className="flex w-full items-center space-x-2">
        <Spinner className="h-4 w-4" />
        <span>Fetching annotations...</span>
      </div>
    );
  }

  const noAnnotations = paginatedAnnotations.length === 0;
  if (noAnnotations) {
    return <JobAnnotationsEmptyState />;
  }

  return (
    <div className="flex w-full flex-1 flex-col" onClick={navigateClickHandler}>
      {paginatedAnnotations.map((annotation) => (
        <JobAnnotationWithLink key={annotation.id} annotation={annotation} />
      ))}

      {hasNextPage && (
        <div className="flex w-full items-center justify-center">
          {isFetchingNextPage ? (
            <div className="space-x-2">
              <Spinner className="h-4 w-4" />
              <span>Fetching annotations...</span>
            </div>
          ) : (
            <button className="btn btn-small btn-depth" onClick={handleShowMore}>
              Show more
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function JobAnnotationWithLink({ annotation }: { annotation: JobAnnotation }) {
  if (!annotation.jobUuid) {
    return <Annotation annotation={annotation} />;
  }

  return (
    <div>
      <JobAnnotationLink annotation={annotation} />
      <Annotation annotation={annotation} />
    </div>
  );
}
